import { Spinner } from "@procore/core-react";
import { useCallback, useEffect, useRef } from "react";

import type { Node, Tree } from "components/objectTree/types";
import type { ModelTreeNodeData } from "features/modelTree/types";

import { MobileObjectTreeRow } from "./MobileObjectTreeRow";
import { SwipeableRow } from "./SwipeableRow";

interface MobileObjectTreeLevelProps {
    tree: Tree<ModelTreeNodeData>;
    drillPath: string;
    onDrillIn: (path: string) => void;
    onLeafTap: (node: Node<ModelTreeNodeData>) => void;
    onLoadMore: () => void;
}

export function MobileObjectTreeLevel({
    tree,
    drillPath,
    onDrillIn,
    onLeafTap,
    onLoadMore,
}: MobileObjectTreeLevelProps) {
    const node = tree[drillPath];
    const hasMoreChildren = node?.hasMoreChildren ?? false;
    // True only while a page fetch is in progress (children already loaded).
    // Listed as a dep so the observer re-attaches after each loading transition.
    const isPaging = Boolean(node?.loading && node?.children);
    const activeRowCloseRef = useRef<(() => void) | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    const handleRowOpen = useCallback((closeFn: () => void) => {
        if (activeRowCloseRef.current !== closeFn) {
            activeRowCloseRef.current?.();
        }
        activeRowCloseRef.current = closeFn;
    }, []);

    const handleDrillIn = useCallback(
        (path: string) => {
            activeRowCloseRef.current?.();
            activeRowCloseRef.current = null;
            onDrillIn(path);
        },
        [onDrillIn],
    );

    useEffect(() => {
        const sentinel = sentinelRef.current;
        const root = scrollContainerRef.current;
        // Skip attaching while a page fetch is in flight — the sentinel is still
        // in the 400px margin, so a new observe() would fire immediately and race
        // the in-flight request. Re-attachment happens when isPaging goes false
        // (success or error), which is exactly when the next page should be allowed.
        if (!sentinel || !root || isPaging) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    onLoadMore();
                }
            },
            { root, rootMargin: "400px 0px" },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [onLoadMore, hasMoreChildren, drillPath, isPaging]);

    // Initial load — no children data yet. Show full-tier spinner.
    if (!node || !node.children) {
        return (
            <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
                <Spinner />
            </div>
        );
    }

    const children = node.children.map((path) => tree[path]).filter((n): n is Node<ModelTreeNodeData> => n != null);

    return (
        <div ref={scrollContainerRef} style={{ height: "100%", overflowY: "auto" }}>
            {children.map((child) => (
                <SwipeableRow key={child.path} node={child} onOpen={handleRowOpen}>
                    <MobileObjectTreeRow node={child} tree={tree} onDrillInto={handleDrillIn} onLeafTap={onLeafTap} />
                </SwipeableRow>
            ))}
            {node.loading && (
                // Paging in progress — keep the list up, show a footer spinner
                // so scroll position and the sentinel are preserved.
                <div
                    data-testid="load-more-spinner"
                    style={{ display: "flex", justifyContent: "center", padding: "8px" }}
                >
                    <Spinner />
                </div>
            )}
            {hasMoreChildren && (
                <div ref={sentinelRef} data-testid="load-more-sentinel" aria-hidden style={{ height: 1 }} />
            )}
        </div>
    );
}
